import React, { useEffect, useState } from 'react';
import { useMutation } from '@common/hooks';
import {
  ExtensionOfferForm,
  ExtensionOfferValues,
  FormValues,
} from './extension-offer-form';
import { ExtensionOfferReview } from './extension-offer-review';
import { Icon, PrimaryButton } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Button, Form, Space } from 'antd';
import {
  CreateExtensionOfferMutation,
  CreateExtensionOfferMutationResponse,
  CreateExtensionOfferMutationVariables,
} from '@working-experience/graphql/mutations';
import { Store } from 'antd/lib/form/interface';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import { EmploymentTypes } from '@common/graphql';

type ExportedPermissionType = {
  value: boolean;
};

type CreateExtensionOfferFlowProps = {
  setTitle: (title: React.ReactNode) => void;
  setDrawerFooter: (footer: React.ReactNode) => void;
  onDrawerClose: () => void;
  assignment: AssignmentType;
};

export type AssignmentType = {
  id: string;
  organizationUnit: {
    id: string;
  };
  startDate: string;
  endDate: string;
  hoursPerShift: number;
  shiftsPerWeek: number;
  lengthInWeeks: number;
  timeoffDescription: string;
  billingRegularRate: number;
  shiftType: string;
  employmentType: EmploymentTypes;
  staffingRequestApplication: {
    id: string;
  };
  lastAssignmentTerm: {
    id: string;
    organizationUnit: {
      id: string;
    };
    overtimeThreshold: number;
  };
  supplier: {
    id: string;
    name: string;
  };
  worksProfile: {
    id: string;
    fullName: string;
  };
  canApproveExtensions: ExportedPermissionType;
  finalEndDate: string;
  staffingRequest: {
    id: string;
    role: {
      id: string;
    };
    roleLevel: string;
    organizationUnit: {
      id: string;
    };
  };
};

function ReviewDrawerTitle({ onBackClick }: { onBackClick: () => void }) {
  return (
    <>
      <Button type="link" onClick={onBackClick}>
        <Icon icon="longArrowLeft" kind="light" />
      </Button>
      <span>Review Extension Offer</span>
    </>
  );
}

export function CreateExtensionOfferFlow({
  assignment,
  setTitle,
  setDrawerFooter,
  onDrawerClose,
}: CreateExtensionOfferFlowProps) {
  const [review, setReview] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [extensionOfferValues, setExtensionOfferValues] =
    useState<ExtensionOfferValues>({} as ExtensionOfferValues);
  const [extensionForm] = Form.useForm();
  const [reviewForm] = Form.useForm();

  const showErrorMessage = () =>
    createMessage('error', 'Failed to send Offer. Please try again.');

  const [createExtensionOffer, { loading: loadingMutation }] = useMutation<
    CreateExtensionOfferMutationResponse,
    CreateExtensionOfferMutationVariables
  >(CreateExtensionOfferMutation, {
    onCompleted: (response) => {
      if (response.createExtensionOffer.errors) {
        showErrorMessage();
        return;
      }

      const approverNames =
        response.createExtensionOffer.extensionOffer?.approvalChains[0]?.currentApprovers.map(
          (approver) => approver.name
        );

      const addOrOnLastName = { type: 'disjunction' };
      // TODO: Remove this once we upgrade to typescript 4.7
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const approvers = new Intl.ListFormat('en', addOrOnLastName).format(
        approverNames
      );

      createMessage(
        'success',
        response.createExtensionOffer.extensionOffer?.state === 'pending'
          ? `Extension Offer has been submitted and is pending approval by ${approvers}.`
          : `Extension Offer sent to ${assignment.supplier.name}`
      );
      onDrawerClose();
    },
    onError: showErrorMessage,
    refetchQueries: ['CandidateAssignmentQuery'],
  });

  const onFormDone = (values: FormValues & { deviated: boolean }) => {
    setTitle(<ReviewDrawerTitle onBackClick={onBackToForm} />);
    setExtensionOfferValues(values);
    setReview(true);
  };

  const onBackToForm = () => {
    setTitle(`Extend ${assignment.worksProfile.fullName}`);
    setReview(false);
  };

  const formFooter = (
    <Space>
      <Button size="large" key="back" onClick={onDrawerClose}>
        Cancel
      </Button>
      <PrimaryButton
        data-testid="continue-button"
        onClick={extensionForm.submit}
        size="large"
        key="submit"
        loading={loadingForm}
      >
        Continue
      </PrimaryButton>
    </Space>
  );

  const reviewFooter = (
    <Space>
      <Button size="large" key="back" onClick={onDrawerClose}>
        Cancel
      </Button>
      <PrimaryButton
        onClick={reviewForm.submit}
        size="large"
        key="submit"
        loading={loadingMutation}
      >
        {assignment.canApproveExtensions.value
          ? 'Send Extension Offer'
          : 'Submit for Approval'}
      </PrimaryButton>
    </Space>
  );

  useEffect(() => {
    setDrawerFooter(review ? reviewFooter : formFooter);
  }, [review, loadingForm, loadingMutation]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReviewDone = ({ extensionReason }: Store) => {
    createExtensionOffer({
      variables: {
        deviated: extensionOfferValues.deviated,
        staffingRequestAssignmentId: assignment.id,
        approvalChainTemplateSource:
          extensionOfferValues.approvalChainTemplateSource,
        input: {
          approvalDocument: extensionOfferValues.approvalDocument?.file,
          startDate: toISODateFormat(extensionOfferValues['startDate']),
          endDate: toISODateFormat(extensionOfferValues['endDate']),
          shiftType: extensionOfferValues.shiftType,
          billingRegularRate: extensionOfferValues.billingRegularRate,
          timeoffDescription: extensionOfferValues.timeoffDescription,
          extensionReason: extensionReason,
          shiftsPerWeek: extensionOfferValues.shiftsPerWeek,
          hoursPerShift: extensionOfferValues.hoursPerShift,
          commentContent: extensionOfferValues.commentContent,
          overtimeThreshold: extensionOfferValues.overtimeThreshold,
        },
      },
    });
  };

  return (
    <>
      {review ? (
        <ExtensionOfferReview
          form={reviewForm}
          newData={extensionOfferValues}
          onFinish={onReviewDone}
          assignment={assignment}
        />
      ) : (
        <ExtensionOfferForm
          form={extensionForm}
          assignment={assignment}
          onFinish={onFormDone}
          setLoading={setLoadingForm}
        />
      )}
    </>
  );
}
